import type { ApprovalDecision } from "@agentry/api-client";
import type { Artifact } from "@agentry/zerovia-contracts";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useApi } from "../../app/ApiProvider";
import { useScope } from "../../app/AuthProvider";
import { useAction, useAsync } from "../../app/useAsync";
import {
	Badge,
	Button,
	Callout,
	Card,
	DefinitionList,
	LinkButton,
	PageHeader,
	StatusBadge,
	TextArea,
} from "../../ui/primitives";
import {
	EmptyState,
	ErrorState,
	isNotFound,
	LoadingState,
	NotFoundState,
	recoveryFor,
} from "../../ui/states";
import { parseProposal } from "./approvals/proposal";
import { formatDateTime } from "./ProgressPages";

type ProposalOption = {
	artifact: Artifact;
	siteId: "default" | "lp" | "newbiz" | "creator";
	title: string;
	description: string;
};

export function ApprovalListPage() {
	const { client } = useApi();
	const scope = useScope();
	const data = useAsync(
		() => client.listApprovals(scope),
		[client, scope.tenantId, scope.projectId],
	);

	return (
		<>
			<PageHeader
				eyebrow="承認・差戻し"
				title="あなたの判断が必要なもの"
				description="品質チェックの結果と根拠を見て、承認するか直してもらうかを選びます。"
			/>
			{data.status === "loading" && <LoadingState />}
			{data.status === "error" && <ErrorState error={data.error} onRetry={data.reload} />}
			{data.status === "ready" &&
				(data.data.length === 0 ? (
					<EmptyState
						title="承認待ちはありません"
						description="判断が必要になるとここに表示されます。"
					/>
				) : (
					<div className="space-y-2">
						{data.data.map((approval) => (
							<Link
								key={approval.id}
								to={`/app/approvals/${approval.id}`}
								className="ag-card block px-4 py-3 transition-colors hover:border-brand-300"
							>
								<div className="flex flex-wrap items-start justify-between gap-3">
									<div className="min-w-0">
										<p className="text-[15px] font-semibold text-ink-900">{approval.title}</p>
										<p className="mt-0.5 line-clamp-2 text-[13px] text-ink-600">
											{approval.summary}
										</p>
									</div>
									<div className="flex shrink-0 items-center gap-2">
										{approval.decision && (
											<Badge tone="neutral">{decisionLabel(approval.decision)}</Badge>
										)}
										<StatusBadge status={approval.status} />
									</div>
								</div>
								{approval.status === "PENDING" && approval.expiresAt && (
									<p className="mt-1.5 text-[12px] text-warn-600">
										期限: {formatDateTime(approval.expiresAt)}
									</p>
								)}
							</Link>
						))}
					</div>
				))}
		</>
	);
}

export function ApprovalDetailPage() {
	const { approvalId = "" } = useParams();
	const { client } = useApi();
	const scope = useScope();
	const navigate = useNavigate();
	const [comment, setComment] = useState("");
	const [selectedArtifactId, setSelectedArtifactId] = useState("");
	const [reloadSeq, setReloadSeq] = useState(0);

	const data = useAsync(async () => {
		const approval = await client.getApproval(scope, approvalId);
		const artifacts = await Promise.all(
			approval.artifactIds.map(async (id) => {
				const artifact = await client.getArtifact(scope, id);
				const content = await client.getArtifactContent(scope, id);
				return { artifact, content: content.content };
			}),
		);
		const proposals: ProposalOption[] = artifacts.flatMap(({ artifact, content }) => {
			if (artifact.id === approval.indexArtifactId) return [];
			const proposal = parseProposal(content);
			if (!proposal) return [];
			return [
				{
					artifact,
					siteId: proposal.siteId,
					title: proposal.content.hero.title.join(" "),
					description: proposal.content.hero.description,
				},
			];
		});
		return { approval, artifacts: artifacts.map(({ artifact }) => artifact), proposals };
	}, [client, scope.tenantId, scope.projectId, approvalId, reloadSeq]);

	const decide = useAction(async (decision: ApprovalDecision) => {
		const selected =
			data.status === "ready"
				? data.data.proposals.find((proposal) => proposal.artifact.id === selectedArtifactId)
				: undefined;
		return client.decideApproval(scope, approvalId, {
			decision,
			selectedArtifactId: selected?.artifact.id,
			selectedArtifactVersion: selected?.artifact.version,
			siteId: selected?.siteId ?? (data.status === "ready" ? data.data.approval.siteId : "default"),
			instructions: comment,
			// 同じ判断の二重送信で承認を二重確定させない
			idempotencyKey: `approval-${approvalId}-${decision}`,
		});
	});

	if (data.status === "loading") return <LoadingState />;
	if (data.status === "error") {
		if (isNotFound(data.error)) {
			return <NotFoundState detail="この承認にはアクセスできません。" />;
		}
		return <ErrorState error={data.error} onRetry={data.reload} />;
	}

	const { approval, artifacts, proposals } = data.data;
	const pending = approval.status === "PENDING";

	return (
		<>
			<PageHeader
				eyebrow={approval.gateType === "APPROVAL" ? "承認" : "品質判定"}
				title={approval.title}
				description={approval.summary}
				actions={
					<LinkButton to={`/app/progress/${approval.executionId}`} variant="secondary">
						進捗を見る
					</LinkButton>
				}
			/>

			{!pending && (
				<Callout
					tone={approval.decision === "APPROVED" ? "success" : "warning"}
					title="判断済みです"
				>
					{approval.decision ? decisionLabel(approval.decision) : "処理済み"}（
					{formatDateTime(approval.decidedAt)}）
					{approval.decision === "CHANGES_REQUESTED" && approval.changeRequestRoute && (
						<p className="mt-1">差戻し先の工程から作業をやり直しています。</p>
					)}
				</Callout>
			)}

			<div className="mt-5 grid gap-4 lg:grid-cols-[1fr_340px]">
				<div className="space-y-4">
					{pending && proposals.length > 0 && (
						<Card
							title="案を選ぶ"
							description="3つの構成案を比較して、採用する案を1つ選んでください"
						>
							<div className="space-y-3">
								{proposals.map((proposal, index) => (
									<label
										key={proposal.artifact.id}
										className="ag-card flex cursor-pointer gap-3 p-4"
									>
										<input
											type="radio"
											name="proposal"
											value={proposal.artifact.id}
											checked={selectedArtifactId === proposal.artifact.id}
											onChange={() => setSelectedArtifactId(proposal.artifact.id)}
											aria-label={`案${index + 1}を選ぶ`}
										/>
										<span>
											<span className="block text-[14px] font-semibold">
												案{index + 1}: {proposal.title}
											</span>
											<span className="mt-1 block text-[13px] text-ink-600">
												{proposal.description}
											</span>
										</span>
									</label>
								))}
							</div>
						</Card>
					)}
					<Card title="判断の材料" description="品質チェックの結果です">
						<ul className="divide-y divide-ink-100">
							{approval.evidence.map((item) => (
								<li
									key={item.label}
									className="flex flex-wrap items-center justify-between gap-2 py-2 first:pt-0 last:pb-0"
								>
									<span className="text-[13.5px] text-ink-700">{item.label}</span>
									<span
										className={
											item.value.startsWith("FAILED")
												? "text-[13.5px] font-semibold text-danger-600"
												: "text-[13.5px] font-semibold text-accent-600"
										}
									>
										{item.value}
									</span>
								</li>
							))}
						</ul>
					</Card>

					{artifacts.length > 0 && (
						<Card title="確認する成果物">
							<ul className="space-y-2">
								{artifacts.map((artifact) => (
									<li
										key={artifact.id}
										className="flex flex-wrap items-center justify-between gap-2"
									>
										<div className="min-w-0">
											<Link
												to={`/app/artifacts/${artifact.id}`}
												className="text-[14px] font-semibold text-ink-900 hover:text-brand-600"
											>
												{artifact.name}
											</Link>
											<p className="text-[12px] text-ink-500">
												v{artifact.version} · {artifact.mimeType} ·{" "}
												{artifact.sizeBytes.toLocaleString()} bytes
											</p>
										</div>
										<LinkButton to={`/app/artifacts/${artifact.id}`} variant="secondary" size="sm">
											内容を見る
										</LinkButton>
									</li>
								))}
							</ul>
						</Card>
					)}
				</div>

				<Card title={pending ? "どうしますか" : "判断の記録"}>
					{pending ? (
						<div className="space-y-3">
							<TextArea
								rows={4}
								value={comment}
								onChange={(event) => setComment(event.target.value)}
								placeholder="直してほしい箇所や、承認の条件があれば書いてください（任意）"
								aria-label="コメント"
							/>
							<Button
								className="w-full"
								disabled={decide.pending || (proposals.length > 0 && !selectedArtifactId)}
								onClick={async () => {
									const result = await decide.run("APPROVED");
									if (result) {
										if (client.mode === "real") navigate("/app/approvals");
										else setReloadSeq((n) => n + 1);
									}
								}}
							>
								この内容で承認する
							</Button>
							<Button
								variant="secondary"
								className="w-full"
								disabled={decide.pending || !comment.trim()}
								onClick={async () => {
									const result = await decide.run("CHANGES_REQUESTED");
									if (result) {
										if (client.mode === "real") navigate("/app/approvals");
										else setReloadSeq((n) => n + 1);
									}
								}}
							>
								直してもらう（差戻し）
							</Button>
							<Button
								variant="danger"
								className="w-full"
								disabled={decide.pending}
								onClick={async () => {
									const result = await decide.run("REJECTED");
									if (result) setReloadSeq((n) => n + 1);
								}}
							>
								却下する
							</Button>

							{decide.error != null && (
								<div
									role="alert"
									className="rounded-lg border border-danger-500/40 bg-danger-100 px-3 py-2"
								>
									<p className="text-[13px] font-bold text-danger-600">
										{recoveryFor(decide.error).title}
									</p>
									<p className="text-[12.5px] text-ink-700">{recoveryFor(decide.error).hint}</p>
									<Button
										size="sm"
										variant="secondary"
										className="mt-2"
										onClick={() => setReloadSeq((n) => n + 1)}
									>
										最新の状態を取得
									</Button>
								</div>
							)}

							<p className="text-[12px] text-ink-500">
								差戻しを選ぶと、決められた工程まで戻って作業をやり直します。品質チェックは省略されません。
							</p>
						</div>
					) : (
						<>
							<DefinitionList
								columns={1}
								items={[
									{
										label: "判断",
										value: approval.decision ? decisionLabel(approval.decision) : "—",
									},
									{ label: "判断者", value: approval.decidedBy ?? "—" },
									{ label: "判断日時", value: formatDateTime(approval.decidedAt) },
								]}
							/>
							<Button
								variant="secondary"
								size="sm"
								className="mt-3 w-full"
								onClick={() => navigate("/app/approvals")}
							>
								承認一覧へ戻る
							</Button>
						</>
					)}
				</Card>
			</div>
		</>
	);
}

function decisionLabel(decision: ApprovalDecision): string {
	switch (decision) {
		case "APPROVED":
			return "承認";
		case "REJECTED":
			return "却下";
		case "CHANGES_REQUESTED":
			return "差戻し";
	}
}
